import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { trim, get, isEmpty } from 'lodash';
import Notification from 'common-ui-components/dist/components/Atoms/Notification';
import InputWithIcon from 'common-ui-components/dist/components/Atoms/InputWithIcon';
import Modal from 'common-ui-components/dist/components/Atoms/Modal';
import Anchor from 'common-ui-components/dist/components/Atoms/Anchor';
import Button from 'common-ui-components/dist/components/Atoms/Button';
import RequiredText from 'common-ui-components/dist/components/Atoms/RequiredText';
import validate from 'common-ui-components/dist/core/utils/validator';
import { getLabelsWithAsterisk } from '../../../utils/utils';
import { EMAIL_INPUT_MAX_LENGTH } from '../../../constants';
import './PasswordPrompt.scss';

const PasswordPrompt = props => {
  const {
    labels,
    config,
    onUserAuthorizationAction,
    pageContent,
    signOutAction,
    shortTermTokeExpiredData,
    onPasswordPromptClose,
    PasswordPromptData,
    clearPasswordPromptError,
  } = props;
  const email = get(shortTermTokeExpiredData, ['message']);
  const headerContent = pageContent ? pageContent.find(item => item.name === 'header') : {};
  const signOutUrl = get(headerContent, 'signOutLink', '#');
  const troubleSignInURL = get(headerContent, 'troublesigningin', '#');
  const [showPasswordPrompt, setPasswordPrompt] = useState(true);
  const [userEmail, setUserEmail] = useState(email);
  const [userInputEmail, setUserInputEmail] = useState({
    touched: false,
    valid: false,
    value: '',
    errorMsg: '',
  });
  const [inputPassword, setInputPassword] = useState({
    value: '',
    valid: false,
    touched: false,
    errorMsg: '',
  });
  const [showInvalidCredError, setInvalidCredError] = useState(false);
  const {
    signIn,
    requiredField,
    notYou,
    signOut,
    emailAddress,
    password,
    cancel,
    havingTroubleSignIn,
    passwordErrorMessage,
    emailValidateMessage,
    invalidEmailAndPasswordError,
    pleaseTryAgain,
  } = labels;
  const { userIcon, eyeIcon, eyeOffIcon, unavailableIconUrl } = config;
  useEffect(() => {
    if (!isEmpty(email)) {
      setUserEmail(email);
    }
  }, [email]);

  useEffect(() => {
    const { code } = PasswordPromptData;
    if (!isEmpty(PasswordPromptData) && code === 804) {
      setInvalidCredError(true);
    }
  }, [PasswordPromptData]);

  const setInputValue = (
    value,
    valid,
    touched = true,
    errorMsg = '',
    callBack = () => {
      /* default callBack handler */
    }
  ) => {
    const data = {
      value,
      valid,
      touched,
      errorMsg,
    };
    callBack(data);
  };

  const handleOnEmailChange = e => {
    let {
      target: { value: inputValue },
    } = e;
    const trimValue = trim(inputValue);
    inputValue = trimValue;
    const valid = validate(inputValue, { isRequired: true, isEmail: true });
    setInputValue(inputValue, valid, valid, '', setUserInputEmail);
  };

  const handleOnEmailBlur = e => {
    let {
      target: { value: inputValue },
    } = e;
    const trimValue = trim(inputValue);
    inputValue = trimValue;
    const valid = validate(inputValue, { isRequired: true, isEmail: true });
    setInputValue(inputValue, valid, true, emailValidateMessage, setUserInputEmail);
  };

  const handleOnChange = e => {
    let {
      target: { value },
    } = e;
    const trimValue = trim(value);
    value = trimValue;
    const valid = validate(value, { isRequired: true });
    setInputValue(value, valid, true, passwordErrorMessage, setInputPassword);
  };

  const onSignOut = e => {
    e.preventDefault();
    signOutAction({ signOutUrl });
  };

  const PasswordPromptCloseHandler = () => {
    setPasswordPrompt(false);
    onPasswordPromptClose({});
    setInvalidCredError(false);
    clearPasswordPromptError({});
  };

  const onSignInHandler = e => {
    e.preventDefault();
    setInvalidCredError(false);
    const { value: userEmailValue, valid: userEmailValid } = userInputEmail;
    const { value: passwordValue, valid: passwordValid } = inputPassword;
    let status = true;
    if (!passwordValid) {
      setInputValue(passwordValue, passwordValid, true, passwordErrorMessage, setInputPassword);
      status = false;
    }
    if (isEmpty(userEmail) && !userEmailValid) {
      setInputValue(userEmailValue, userEmailValid, true, emailValidateMessage, setUserInputEmail);
      status = false;
    }
    if (status) {
      onUserAuthorizationAction({ email: userEmail || userEmailValue, password: passwordValue });
    }
  };

  const errorNotification = () =>
    showInvalidCredError && (
      <Notification
        className="col-xs-12 invalid-email-password-notification"
        variation="siteWide"
        type="serverError"
        title={invalidEmailAndPasswordError}
        description={pleaseTryAgain}
        icon={unavailableIconUrl}
        elementLocator="notification-invalid-email-and-password-error"
      />
    );

  const modalHeading = () => (
    <div data-locator="signin-modal-header">
      <Notification title={signIn} icon={userIcon} elementLocator="sign-in" />
    </div>
  );

  const modalProps = {
    className: 'modal__Content sign-in-modal',
    shouldCloseOnOverlayClick: true,
    isOpen: showPasswordPrompt,
    onRequestClose: () => PasswordPromptCloseHandler(),
    heading: modalHeading(),
    errorNotification: errorNotification(),
    elementLocator: 'password-prompt-modal',
  };

  const signOutProps = {
    className: 'sign-out-link',
    elementLocator: 'password-prompt-sign-out-link',
    href: signOutUrl,
    onClick: e => onSignOut(e),
  };

  return (
    <Modal {...modalProps}>
      <>
        <form className="row" onSubmit={onSignInHandler}>
          <div className="col-sm-6 col-xs-6 col-lg-6" data-locator="password-prompt-subheader">
            <RequiredText
              requiredField={requiredField}
              elementLocator="password-prompt-required-field-text"
            />
          </div>
          <div className="col-sm-6 col-xs-6 col-lg-6 sign-out-section end-xs">
            <span data-locator="password-prompt-not-you-text">{notYou}</span>
            <Anchor {...signOutProps}>{signOut}</Anchor>
          </div>
          <div className="col-xs-12 email-section" data-locator="email-address-section">
            <span className="email-address-label" data-locator="email-label">
              {getLabelsWithAsterisk(emailAddress, isEmpty(userEmail))}
            </span>
            {!isEmpty(userEmail) && (
              <span className="user-email" data-locator="emaillabel-label">
                {userEmail}
              </span>
            )}
            {isEmpty(userEmail) && (
              <InputWithIcon
                id="emailAddress"
                type="email"
                name="emailAddress"
                maxLength={EMAIL_INPUT_MAX_LENGTH}
                className="sign-in-input-field"
                autoComplete="email"
                {...userInputEmail}
                {...{
                  onChange: e => {
                    handleOnEmailChange(e);
                  },
                  onBlur: e => {
                    handleOnEmailBlur(e);
                  },
                }}
                elementLocator="email"
              />
            )}
          </div>
          <div className="col-xs-12">
            <div className="password-label" data-locator="password-label">
              {getLabelsWithAsterisk(password, true)}
            </div>
            <InputWithIcon
              id="password"
              type="password"
              name="password"
              maxInputLength="100"
              className="sign-in-password-input"
              eyeIcon={eyeIcon}
              eyeOffIcon={eyeOffIcon}
              autoComplete="current-password"
              {...inputPassword}
              {...{
                onChange: e => {
                  handleOnChange(e);
                },
                onBlur: e => {
                  handleOnChange(e);
                },
              }}
              elementLocator="password"
            />
          </div>
          <div className="col-xs-12 having-trouble-sign-in-link-section">
            <Anchor
              to={troubleSignInURL}
              className="having-trouble-sign-in-link"
              elementLocator="password-prompt-having-trouble-sign-in-link"
            >
              {havingTroubleSignIn}
            </Anchor>
          </div>
          <div className="col-xs-12">
            <Button
              type="submit"
              className="primary button-sign-in"
              elementLocator="password-prompt-sign-in-button"
              {...{ onClick: e => onSignInHandler(e) }}
            >
              {signIn}
            </Button>
          </div>
          <div className="col-xs-12 cancel-link-section">
            <Anchor
              to="#"
              className="cancel-link"
              elementLocator="password-prompt-sign-in-cancel-link"
              {...{ onClick: () => PasswordPromptCloseHandler() }}
            >
              {cancel}
            </Anchor>
          </div>
        </form>
      </>
    </Modal>
  );
};

PasswordPrompt.propTypes = {
  showPasswordPrompt: PropTypes.bool,
  labels: PropTypes.objectOf(PropTypes.string),
  config: PropTypes.objectOf(PropTypes.string),
  shortTermTokeExpiredData: PropTypes.objectOf(PropTypes.string),
  onUserAuthorizationAction: PropTypes.func,
  pageContent: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool])
  ),
  signOutAction: PropTypes.func,
  onPasswordPromptClose: PropTypes.func,
  PasswordPromptData: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  clearPasswordPromptError: PropTypes.func,
};

PasswordPrompt.defaultProps = {
  showPasswordPrompt: false,
  labels: {},
  config: {},
  shortTermTokeExpiredData: {},
  PasswordPromptData: {},
  pageContent: [],
  onUserAuthorizationAction: () => {
    /* default onUserAuthorizationAction handler */
  },
  signOutAction: () => {
    /* default signOutAction handler */
  },
  onPasswordPromptClose: () => {
    /* default onPasswordPromptClose handler */
  },
  clearPasswordPromptError: () => {
    /* default clearPasswordPromptError handler */
  },
};

export default PasswordPrompt;
